import { useState, useEffect, useMemo, useRef } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { ArrowLeft, RefreshCw, Filter, ChevronDown, ChevronUp, Clock, AlertTriangle, Calendar, CalendarDays, CalendarRange, Landmark } from "lucide-react";

const CURRENCIES = [
  { code: "USD", flag: "\u{1F1FA}\u{1F1F8}" },
  { code: "EUR", flag: "\u{1F1EA}\u{1F1FA}" },
  { code: "GBP", flag: "\u{1F1EC}\u{1F1E7}" },
  { code: "JPY", flag: "\u{1F1EF}\u{1F1F5}" },
  { code: "CHF", flag: "\u{1F1E8}\u{1F1ED}" },
  { code: "CAD", flag: "\u{1F1E8}\u{1F1E6}" },
  { code: "AUD", flag: "\u{1F1E6}\u{1F1FA}" },
  { code: "NZD", flag: "\u{1F1F3}\u{1F1FF}" },
  { code: "CNY", flag: "\u{1F1E8}\u{1F1F3}" },
];

const IMPACT_MAP = {
  High:   { label: "Alto",   color: "#EF4444", bg: "rgba(239,68,68,0.15)",  border: "rgba(239,68,68,0.4)" },
  Medium: { label: "Medio",  color: "#F7931A", bg: "rgba(247,147,26,0.15)", border: "rgba(247,147,26,0.4)" },
  Low:    { label: "Basso",  color: "#FACC15", bg: "rgba(250,204,21,0.15)", border: "rgba(250,204,21,0.4)" },
  Holiday:{ label: "Festività", color: "#A78BFA", bg: "rgba(167,139,250,0.15)", border: "rgba(167,139,250,0.4)" },
};

const ALL_IMPACTS = ["High", "Medium", "Low", "Holiday"];

const STORAGE_KEY = "mm_cal_filters";
const getFilters = () => {
  try {
    const s = sessionStorage.getItem(STORAGE_KEY);
    if (s) {
      const f = JSON.parse(s);
      if (!f.impacts.includes("Holiday") && f.impacts.length === 3) f.impacts.push("Holiday");
      return f;
    }
  } catch {}
  return { currencies: CURRENCIES.map(c => c.code), impacts: [...ALL_IMPACTS] };
};
const saveFilters = (f) => sessionStorage.setItem(STORAGE_KEY, JSON.stringify(f));

// --- Date helpers ---
const toLocalDate = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
};
const today = () => { const d = new Date(); return toLocalDate(d); };
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate()+1); return toLocalDate(d); };
const getWeekRange = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offset * 7);
  const dow = d.getDay();
  const mon = new Date(d); mon.setDate(d.getDate() - ((dow + 6) % 7));
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { start: toLocalDate(mon), end: toLocalDate(sun) };
};
const getDayKey = (dateStr) => {
  try { return new Date(dateStr).toISOString().slice(0, 10); } catch { return "unknown"; }
};
const formatTime = (dateStr) => {
  try { return new Date(dateStr).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", hour12: false }); } catch { return "\u2014"; }
};
const formatDayHeader = (dateStr) => {
  try { return new Date(dateStr).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); } catch { return dateStr; }
};

// --- Period definitions ---
const PERIODS = [
  { key: "today",      label: "Oggi",              icon: Calendar },
  { key: "tomorrow",   label: "Domani",            icon: Calendar },
  { key: "this-week",  label: "Questa Settimana",  icon: CalendarDays },
  { key: "next-week",  label: "Prossima Settimana",icon: CalendarDays },
  { key: "last-week",  label: "Settimana Scorsa",  icon: CalendarDays },
  { key: "custom",     label: "Periodo",           icon: CalendarRange },
];

export default function CalendarioEconomico() {
  const [allEvents, setAllEvents] = useState([]);      // main api data (current week)
  const [historyEvents, setHistoryEvents] = useState([]); // fetched from history
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState(getFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [fetchInfo, setFetchInfo] = useState("");
  const [period, setPeriod] = useState("this-week");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const customRef = useRef(null);

  // Fetch main weekly data
  const fetchWeekly = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API}/economic-calendar`);
      setAllEvents(res.data.events || []);
      const cached = res.data.cached ? " (cache)" : "";
      setFetchInfo(`Aggiornato: ${new Date(res.data.fetched_at).toLocaleTimeString("it-IT")}${cached}`);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  // Fetch range data (TradingView API for any date range)
  const fetchRange = async (start, end) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/economic-calendar/range`, { params: { start, end } });
      setHistoryEvents(res.data.events || []);
      const cached = res.data.cached ? " (cache)" : "";
      setFetchInfo(`Aggiornato: ${new Date(res.data.fetched_at).toLocaleTimeString("it-IT")}${cached}`);
    } catch (e) { console.error(e); setHistoryEvents([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchWeekly(); }, []);

  // When period changes, fetch data if needed
  useEffect(() => {
    if (period === "last-week") {
      const { start, end } = getWeekRange(-1);
      fetchRange(start, end);
    } else if (period === "next-week") {
      const { start, end } = getWeekRange(1);
      fetchRange(start, end);
    } else if (period === "custom" && customFrom && customTo) {
      fetchRange(customFrom, customTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const applyCustomRange = () => {
    if (customFrom && customTo) {
      fetchRange(customFrom, customTo);
      setShowCustomPicker(false);
    }
  };

  const handlePeriodChange = (key) => {
    setPeriod(key);
    if (key === "custom") {
      setShowCustomPicker(true);
    } else {
      setShowCustomPicker(false);
    }
  };

  // Determine which events to display based on selected period
  const baseEvents = useMemo(() => {
    if (period === "today") {
      const t = today();
      return allEvents.filter(ev => getDayKey(ev.date) === t);
    }
    if (period === "tomorrow") {
      const tm = tomorrow();
      return allEvents.filter(ev => getDayKey(ev.date) === tm);
    }
    if (period === "this-week") {
      return allEvents;
    }
    // For last-week, next-week, custom: use history
    return historyEvents;
  }, [period, allEvents, historyEvents]);

  // Apply currency + impact filters
  const updateFilters = (newF) => { setFilters(newF); saveFilters(newF); };
  const toggleCurrency = (code) => {
    const c = filters.currencies.includes(code) ? filters.currencies.filter(x => x !== code) : [...filters.currencies, code];
    if (c.length === 0) return;
    updateFilters({ ...filters, currencies: c });
  };
  const toggleImpact = (val) => {
    const i = filters.impacts.includes(val) ? filters.impacts.filter(x => x !== val) : [...filters.impacts, val];
    if (i.length === 0) return;
    updateFilters({ ...filters, impacts: i });
  };
  const selectAll = () => updateFilters({ currencies: CURRENCIES.map(c => c.code), impacts: [...ALL_IMPACTS] });
  const presetUsdHigh = () => updateFilters({ currencies: ["USD"], impacts: ["High"] });

  const filtered = useMemo(() => {
    return baseEvents.filter(ev =>
      filters.currencies.includes(ev.country) && filters.impacts.includes(ev.impact)
    );
  }, [baseEvents, filters]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(ev => {
      const key = getDayKey(ev.date);
      if (!map[key]) map[key] = { dayKey: key, dateStr: ev.date, events: [] };
      map[key].events.push(ev);
    });
    return Object.values(map).sort((a, b) => a.dayKey.localeCompare(b.dayKey));
  }, [filtered]);

  const currencyObj = (code) => CURRENCIES.find(c => c.code === code);

  // Period subtitle
  const periodSubtitle = useMemo(() => {
    if (period === "today") return `Eventi di oggi \u2014 ${new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}`;
    if (period === "tomorrow") { const d = new Date(); d.setDate(d.getDate()+1); return `Eventi di domani \u2014 ${d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}`; }
    if (period === "this-week") return "News macro della settimana corrente";
    if (period === "next-week") return "News macro della prossima settimana";
    if (period === "last-week") return "News macro della settimana scorsa";
    if (period === "custom" && customFrom && customTo) return `Periodo: ${new Date(customFrom+"T00:00").toLocaleDateString("it-IT")} \u2013 ${new Date(customTo+"T00:00").toLocaleDateString("it-IT")}`;
    return "Seleziona un periodo personalizzato";
  }, [period, customFrom, customTo]);

  if (loading && allEvents.length === 0) {
    return (
      <div className="page-container flex items-center justify-center" data-testid="calendario-economico-page">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
          <span className="text-sm" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>CARICAMENTO NEWS...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="calendario-economico-page">
      <PageHero image={HERO_IMAGES.calendario} kicker="Strumenti" title="Calendario Economico" subtitle={periodSubtitle} />
      <SectionBg type="charts" />
      <div className="container py-8 relative">
        {/* Back + Title */}
        <Link to="/strumenti" className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-lg transition-all hover:bg-white/5" style={{ color: "var(--mm-gold)" }} data-testid="back-to-strumenti">
          <ArrowLeft size={20} /><span style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Torna agli Strumenti</span>
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div />
          <button
            onClick={() => fetchWeekly(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all self-start"
            style={{ backgroundColor: "rgba(47,216,168,0.1)", border: "1px solid rgba(47,216,168,0.3)", color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "AGGIORNAMENTO..." : "AGGIORNA"}
          </button>
        </div>

        {/* Quick Period Buttons */}
        <div className="mb-6" data-testid="period-buttons">
          <div className="flex flex-wrap gap-2">
            {PERIODS.map(p => {
              const active = period === p.key;
              const Icon = p.icon;
              return (
                <button
                  key={p.key}
                  onClick={() => handlePeriodChange(p.key)}
                  className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all"
                  style={{
                    backgroundColor: active ? "rgba(47,216,168,0.15)" : "rgba(255,255,255,0.03)",
                    border: active ? "1px solid var(--mm-gold)" : "1px solid var(--mm-border)",
                    color: active ? "var(--mm-gold)" : "var(--mm-text-muted)",
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                  data-testid={`period-${p.key}`}
                >
                  <Icon size={14} />
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Custom Date Range Picker */}
          {showCustomPicker && (
            <div ref={customRef} className="glass-card mt-3 p-4 flex flex-col sm:flex-row items-start sm:items-end gap-3" data-testid="custom-date-picker">
              <div className="flex-1 w-full sm:w-auto">
                <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>Da</label>
                <input
                  type="date"
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)}
                  className="admin-input text-sm"
                  style={{ minHeight: "40px", padding: "8px 12px" }}
                  data-testid="custom-date-from"
                />
              </div>
              <div className="flex-1 w-full sm:w-auto">
                <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>A</label>
                <input
                  type="date"
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)}
                  className="admin-input text-sm"
                  style={{ minHeight: "40px", padding: "8px 12px" }}
                  data-testid="custom-date-to"
                />
              </div>
              <button
                onClick={applyCustomRange}
                disabled={!customFrom || !customTo}
                className="px-5 py-2 rounded-lg text-sm font-bold transition-all"
                style={{
                  backgroundColor: customFrom && customTo ? "var(--mm-gold)" : "rgba(255,255,255,0.05)",
                  color: customFrom && customTo ? "#000" : "var(--mm-text-muted)",
                  fontFamily: "'Space Grotesk', sans-serif",
                  opacity: customFrom && customTo ? 1 : 0.5,
                  minHeight: "40px",
                }}
                data-testid="custom-date-apply"
              >
                CERCA
              </button>
            </div>
          )}
        </div>

        {/* Filter Bar */}
        {/* Preset rapido */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button
            onClick={presetUsdHigh}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold uppercase transition-all hover:scale-[1.02]"
            style={{
              backgroundColor: filters.currencies.length === 1 && filters.currencies[0] === "USD" && filters.impacts.length === 1 && filters.impacts[0] === "High" ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.5)",
              color: "#EF4444",
              fontFamily: "'Space Grotesk', sans-serif",
              letterSpacing: "0.06em"
            }}
            data-testid="preset-usd-high-btn"
          >
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#EF4444" }} />
            Solo USD · Impatto Massimo
          </button>
          <button
            onClick={selectAll}
            className="px-4 py-2.5 rounded-lg text-xs font-bold uppercase transition-all hover:bg-white/5"
            style={{ border: "1px solid var(--mm-border)", color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.06em" }}
            data-testid="preset-reset-btn"
          >
            Mostra tutto
          </button>
        </div>

        <div className="glass-card mb-6" style={{ overflow: "visible" }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full flex items-center justify-between p-4"
            data-testid="toggle-filters-btn"
          >
            <div className="flex items-center gap-2">
              <Filter size={16} style={{ color: "var(--mm-gold)" }} />
              <span className="text-sm font-semibold" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>FILTRI</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(47,216,168,0.15)", color: "var(--mm-gold)" }}>
                {filters.currencies.length} valute &middot; {filters.impacts.length} impatti
              </span>
            </div>
            {showFilters ? <ChevronUp size={18} style={{ color: "var(--mm-text-muted)" }} /> : <ChevronDown size={18} style={{ color: "var(--mm-text-muted)" }} />}
          </button>

          {showFilters && (
            <div className="px-4 pb-4 space-y-4 border-t" style={{ borderColor: "var(--mm-border)" }}>
              <div className="pt-4 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>Valute</p>
                <button onClick={selectAll} className="text-xs px-2 py-1 rounded transition-colors hover:bg-white/5" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }} data-testid="filter-select-all">RESET TUTTI</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {CURRENCIES.map(c => {
                  const active = filters.currencies.includes(c.code);
                  return (
                    <button key={c.code} onClick={() => toggleCurrency(c.code)}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5"
                      style={{
                        backgroundColor: active ? "rgba(47,216,168,0.12)" : "rgba(255,255,255,0.03)",
                        border: active ? "1px solid var(--mm-gold)" : "1px solid var(--mm-border)",
                        color: active ? "var(--mm-gold)" : "var(--mm-text-muted)",
                        fontFamily: "'Space Grotesk', sans-serif"
                      }}
                      data-testid={`filter-currency-${c.code}`}
                    >
                      <span>{c.flag}</span>{c.code}
                    </button>
                  );
                })}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase mb-2" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>Impatto</p>
                <div className="flex gap-2">
                  {Object.entries(IMPACT_MAP).map(([key, imp]) => {
                    const active = filters.impacts.includes(key);
                    return (
                      <button key={key} onClick={() => toggleImpact(key)}
                        className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2"
                        style={{
                          backgroundColor: active ? imp.bg : "rgba(255,255,255,0.03)",
                          border: active ? `1px solid ${imp.border}` : "1px solid var(--mm-border)",
                          color: active ? imp.color : "var(--mm-text-muted)",
                          fontFamily: "'Space Grotesk', sans-serif"
                        }}
                        data-testid={`filter-impact-${key}`}
                      >
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: imp.color, opacity: active ? 1 : 0.3 }} />
                        {imp.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Loading overlay for period changes */}
        {loading && allEvents.length > 0 && (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
          </div>
        )}

        {/* Events Table */}
        {!loading && grouped.length === 0 ? (
          <div className="glass-card p-12 text-center" data-testid="no-events">
            <AlertTriangle size={40} className="mx-auto mb-4" style={{ color: "var(--mm-text-muted)" }} />
            <p className="text-lg font-semibold" style={{ color: "var(--mm-text-secondary)", fontFamily: "'Space Grotesk', sans-serif" }}>
              Nessun evento trovato
            </p>
            <p className="text-sm mt-1" style={{ color: "var(--mm-text-muted)" }}>
              {period === "custom" && (!customFrom || !customTo)
                ? "Seleziona le date e premi CERCA"
                : period === "next-week"
                  ? "I dati della prossima settimana potrebbero non essere ancora disponibili"
                  : "Prova a modificare i filtri o il periodo per visualizzare pi\u00f9 risultati"}
            </p>
          </div>
        ) : !loading && (
          <div className="space-y-6" data-testid="events-list">
            {grouped.map(group => (
              <div key={group.dayKey} data-testid={`day-group-${group.dayKey}`}>
                {/* Day Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(47,216,168,0.1)" }}>
                    <Clock size={16} style={{ color: "var(--mm-gold)" }} />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold capitalize" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>
                    {formatDayHeader(group.dateStr)}
                  </h2>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.05)", color: "var(--mm-text-muted)" }}>
                    {group.events.length} eventi
                  </span>
                </div>

                {/* Desktop Table */}
                <div className="hidden sm:block glass-card overflow-hidden rounded-xl">
                  <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                    <thead>
                      <tr style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
                        <th className="text-left px-4 py-3 text-xs font-semibold uppercase" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif", width: "70px" }}>Ora</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold uppercase" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif", width: "80px" }}>Valuta</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold uppercase" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif", width: "80px" }}>Impatto</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold uppercase" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>Evento</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold uppercase" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif", width: "90px" }}>Previsione</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold uppercase" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif", width: "90px" }}>Precedente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.events.map((ev, i) => {
                        const curr = currencyObj(ev.country);
                        return (
                          <tr key={`${ev.title}-${ev.date}-${i}`} className="transition-colors"
                            style={{ borderBottom: i < group.events.length - 1 ? "1px solid var(--mm-border)" : "none", backgroundColor: "transparent" }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.02)"}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
                            data-testid={`event-row-${i}`}
                          >
                            <td className="px-4 py-3"><span className="text-sm font-mono font-semibold" style={{ color: "var(--mm-text-primary)" }}>{formatTime(ev.date)}</span></td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                {curr && <span className="text-sm">{curr.flag}</span>}
                                <span className="text-xs font-bold" style={{ color: "var(--mm-text-primary)", fontFamily: "'Space Grotesk', sans-serif" }}>{ev.country}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3"><div className="flex justify-center"><ImpactBar impact={ev.impact} /></div></td>
                            <td className="px-4 py-3">
                              <span className="text-sm" style={{ color: "var(--mm-text-primary)" }}>{ev.title}</span>
                              {ev.impact === "Holiday" && ev.country === "USD" && (
                                <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5" style={{ color: "#A78BFA", backgroundColor: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.35)" }}>Banche USA chiuse</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right"><span className="text-sm font-mono" style={{ color: ev.forecast ? "var(--mm-text-primary)" : "var(--mm-text-muted)" }}>{ev.forecast || "\u2014"}</span></td>
                            <td className="px-4 py-3 text-right"><span className="text-sm font-mono" style={{ color: ev.previous ? "var(--mm-text-secondary)" : "var(--mm-text-muted)" }}>{ev.previous || "\u2014"}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="sm:hidden space-y-2">
                  {group.events.map((ev, i) => {
                    const imp = IMPACT_MAP[ev.impact] || IMPACT_MAP.Low;
                    const curr = currencyObj(ev.country);
                    return (
                      <div key={`${ev.title}-${ev.date}-${i}`} className="glass-card p-3 rounded-xl flex gap-3" style={{ borderLeft: `3px solid ${imp.color}` }} data-testid={`event-card-mobile-${i}`}>
                        <div className="flex flex-col items-center justify-center min-w-[50px]">
                          <span className="text-xs font-mono font-bold" style={{ color: "var(--mm-text-primary)" }}>{formatTime(ev.date)}</span>
                          <div className="flex items-center gap-1 mt-1">
                            {curr && <span className="text-xs">{curr.flag}</span>}
                            <span className="text-[10px] font-bold" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>{ev.country}</span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <ImpactBar impact={ev.impact} />
                            <span className="text-sm font-medium truncate" style={{ color: "var(--mm-text-primary)" }}>{ev.title}</span>
                          </div>
                          {ev.impact === "Holiday" && ev.country === "USD" && (
                            <span className="inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 mb-1" style={{ color: "#A78BFA", backgroundColor: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.35)" }}>Banche USA chiuse</span>
                          )}
                          <div className="flex gap-4 text-xs" style={{ color: "var(--mm-text-muted)" }}>
                            {ev.forecast && <span>Prev: <strong style={{ color: "var(--mm-text-secondary)" }}>{ev.forecast}</strong></span>}
                            {ev.previous && <span>Prec: <strong style={{ color: "var(--mm-text-secondary)" }}>{ev.previous}</strong></span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer Info */}
        <div className="flex items-center justify-between mt-6 text-xs" style={{ color: "var(--mm-text-muted)" }}>
          <span>{filtered.length} eventi visualizzati su {baseEvents.length} totali</span>
          <span>{fetchInfo}</span>
        </div>
        <p className="text-xs text-center mt-2" style={{ color: "var(--mm-text-muted)" }}>
          Dati aggiornati automaticamente. I filtri si resettano al logout.
        </p>
      </div>
    </div>
  );
}

function ImpactBar({ impact }) {
  if (impact === "Holiday") {
    return (
      <div title="Festività" data-testid="impact-bar-Holiday">
        <Landmark size={16} style={{ color: IMPACT_MAP.Holiday.color }} />
      </div>
    );
  }
  const bars = impact === "High" ? 3 : impact === "Medium" ? 2 : 1;
  const imp = IMPACT_MAP[impact] || IMPACT_MAP.Low;
  return (
    <div className="flex gap-0.5 items-end" title={imp.label} data-testid={`impact-bar-${impact}`}>
      {[1, 2, 3].map(n => (
        <div key={n} className="rounded-sm transition-all"
          style={{
            width: "5px",
            height: n === 1 ? "8px" : n === 2 ? "12px" : "16px",
            backgroundColor: n <= bars ? imp.color : "rgba(255,255,255,0.08)",
          }}
        />
      ))}
    </div>
  );
}
